import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import JSZip from "jszip";
import { toast } from "react-toastify";
import { BiCopy, BiEdit, BiRefresh, BiTrash } from "react-icons/bi";
import { FaCamera } from "react-icons/fa";
import { Archive } from "lucide-react";
import Breadcrumbs from "../../components/BreadCrumbs";
import StyledHeadingText from "../../components/Fonts/StyledHeadingText";
import SearchBar from "../../components/searchBar/SearchBar";
import Button from "../../components/buttons/Button";
import InputField from "../../components/inputFields/InputField";
import AsyncFormikDropdown from "../../components/inputFields/AsyncFormikDropDown";
import Table from "../../components/tables/Table";
import Pagination from "../../components/tables/pagination/Pagination";
import ShowModal from "../../utils/ShowModal";
import { BASE_URL } from "../../constants/apis";
import { searchProductWithAkaName } from "../../services/search";
import {
    deleteShopProductDimension,
    getShopProductDimensions,
    patchShopProductDimension,
    postShopProductDimension,
} from "../../services/shopProductDimensions";
import { confirmDanger } from "../../utils/confirmDialog";
import { getUserToken } from "../../helpers/local_storage_helpers";

function resolveMediaUrl(url) {
    if (url == null || url === "") return "";
    const s = String(url).trim();
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    if (s.startsWith("/")) return `${BASE_URL}${s}`;
    return `${BASE_URL}/${s.replace(/^\//, "")}`;
}

function formatDimensionsRow(row) {
    if (row?.dimensions) return row.dimensions;
    const l = row?.length_cm;
    const w = row?.width_cm;
    const h = row?.height_cm;
    if (l != null && w != null && h != null) {
        return `${Number(l).toFixed(2)} x ${Number(w).toFixed(2)} x ${Number(h).toFixed(2)} cm`;
    }
    return "—";
}

/** Plain-text block for clipboard / sharing */
function buildDimensionCopyText(row) {
    const name = row?.product_name || "—";
    const dims = formatDimensionsRow(row);
    const w =
        row?.weight_kg != null && row.weight_kg !== ""
            ? `${Number(row.weight_kg).toFixed(2)} kg`
            : "—";
    return [`Product: ${name}`, `Dimensions (L × W × H): ${dims}`, `Weight: ${w}`].join("\n");
}

function sanitizeFolderName(name) {
    const s = String(name || "product")
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 80);
    return s || "product";
}

function extFromUrl(url) {
    const m = String(url).match(/\.(jpe?g|png|webp|gif)(\?|#|$)/i);
    if (!m) return ".jpg";
    const e = m[1].toLowerCase();
    return e === "jpeg" ? ".jpg" : `.${e}`;
}

async function fetchImageBlob(absUrl) {
    try {
        const res = await axios.get(absUrl, {
            responseType: "blob",
            headers: {
                Authorization: `Token ${getUserToken()}`,
            },
        });
        return res.data;
    } catch {
        // Public /media/ files are often same-origin blocked with auth header (CORS preflight)
        const res = await axios.get(absUrl, { responseType: "blob" });
        return res.data;
    }
}

async function downloadDimensionPhotosZip(row) {
    const folder = sanitizeFolderName(row.product_name);
    const zip = new JSZip();
    const root = zip.folder(folder);
    if (!root) throw new Error("zip folder");

    const slots = [
        { fileBase: "length_photo", url: row.length_photo },
        { fileBase: "width_photo", url: row.width_photo },
        { fileBase: "height_photo", url: row.height_photo },
        { fileBase: "weight_photo", url: row.weight_photo },
    ];

    let added = 0;
    for (const { fileBase, url } of slots) {
        const abs = resolveMediaUrl(url);
        if (!abs) continue;
        try {
            const blob = await fetchImageBlob(abs);
            root.file(`${fileBase}${extFromUrl(abs)}`, blob);
            added += 1;
        } catch (err) {
            console.warn(`ZIP skip ${fileBase}:`, err);
        }
    }

    if (added === 0) {
        throw new Error("NO_PHOTOS");
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const filename = `${folder}.zip`;
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
}

function mapShopProductSearchToOptions(response) {
    const raw = response?.data?.results ?? response?.data;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((pro) => ({
        value: pro.id,
        label: pro.aka_name ?? pro.product_name ?? "Product",
        image: pro.image ?? pro.product_image ?? pro.thumbnail ?? pro.cover_image ?? pro.cover,
    }));
}

const shopProductFormatOptionLabel = (option) => {
    const src = resolveMediaUrl(option.image);
    return (
        <div className="flex items-center gap-2 py-0.5 min-w-0">
            {src ? (
                <img src={src} alt="" className="h-8 w-8 rounded object-cover flex-shrink-0 border border-gray-100" />
            ) : (
                <div className="h-8 w-8 rounded bg-gray-200 flex-shrink-0" />
            )}
            <span className="text-sm truncate">{option.label}</span>
        </div>
    );
};

function TableSkeletonRows({ cols = 7, rows = 8 }) {
    return (
        <div className="mt-2 rounded-xl border border-gray-200 overflow-hidden bg-white">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {Array.from({ length: cols }).map((_, i) => (
                            <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                                &nbsp;
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {Array.from({ length: rows }).map((_, ri) => (
                        <tr key={ri}>
                            {Array.from({ length: cols }).map((_, ci) => (
                                <td key={ci} className="px-4 py-3">
                                    <div className="h-4 bg-gray-200 rounded animate-pulse w-full max-w-[8rem]" />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PhotoThumbButton({ url, onOpen, title }) {
    const src = resolveMediaUrl(url);
    if (!src) {
        return (
            <div
                className="h-9 w-9 rounded-md border border-dashed border-gray-300 bg-gray-100 flex items-center justify-center text-gray-400"
                title={title || "No photo"}
            >
                <FaCamera size={14} />
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={() => onOpen(src)}
            className="h-9 w-9 rounded-md overflow-hidden border border-gray-200 hover:ring-2 hover:ring-primary/40 focus:outline-none focus:ring-2 focus:ring-primary"
            title={title || "View photo"}
        >
            <img src={src} alt="" className="h-full w-full object-cover" />
        </button>
    );
}

function DimensionImageField({ label, name, file, onFileChange, existingUrl }) {
    const [blobUrl, setBlobUrl] = useState("");
    useEffect(() => {
        if (!file) {
            setBlobUrl("");
            return;
        }
        const u = URL.createObjectURL(file);
        setBlobUrl(u);
        return () => URL.revokeObjectURL(u);
    }, [file]);
    const preview = blobUrl || resolveMediaUrl(existingUrl);

    return (
        <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">{label}</span>
            <div className="flex items-center gap-3">
                <div className="h-16 w-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                    {preview ? (
                        <img src={preview} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <FaCamera className="text-gray-300" size={22} />
                    )}
                </div>
                <label className="cursor-pointer inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700">
                    Choose file
                    <input
                        type="file"
                        name={name}
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                    />
                </label>
                {file && (
                    <button type="button" className="text-xs text-red-600 font-medium" onClick={() => onFileChange(null)}>
                        Clear
                    </button>
                )}
            </div>
        </div>
    );
}

const dimensionSchema = Yup.object({
    shop_product: Yup.string().required("Select a shop product"),
    length_cm: Yup.number().typeError("Enter a number").min(0).required("Required"),
    width_cm: Yup.number().typeError("Enter a number").min(0).required("Required"),
    height_cm: Yup.number().typeError("Enter a number").min(0).required("Required"),
    weight_kg: Yup.number().typeError("Enter a number").min(0).required("Required"),
});

function DimensionFormDrawer({ open, onClose, editing, onSaved }) {
    const [lengthFile, setLengthFile] = useState(null);
    const [widthFile, setWidthFile] = useState(null);
    const [heightFile, setHeightFile] = useState(null);
    const [weightFile, setWeightFile] = useState(null);

    useEffect(() => {
        if (!open) {
            setLengthFile(null);
            setWidthFile(null);
            setHeightFile(null);
            setWeightFile(null);
        }
    }, [open, editing]);

    const initialOptions = useMemo(() => {
        if (!editing?.shop_product) return [];
        return [
            {
                value: editing.shop_product,
                label: editing.product_name || "Product",
                image: editing.product_image,
            },
        ];
    }, [editing]);

    const initialValues = useMemo(
        () => ({
            shop_product: editing?.shop_product || "",
            length_cm: editing?.length_cm ?? "",
            width_cm: editing?.width_cm ?? "",
            height_cm: editing?.height_cm ?? "",
            weight_kg: editing?.weight_kg ?? "",
        }),
        [editing]
    );

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const fd = new FormData();
            fd.append("shop_product", values.shop_product);
            fd.append("length_cm", String(values.length_cm));
            fd.append("width_cm", String(values.width_cm));
            fd.append("height_cm", String(values.height_cm));
            fd.append("weight_kg", String(values.weight_kg));
            if (lengthFile) fd.append("length_photo", lengthFile);
            if (widthFile) fd.append("width_photo", widthFile);
            if (heightFile) fd.append("height_photo", heightFile);
            if (weightFile) fd.append("weight_photo", weightFile);

            let res;
            if (editing?.id) {
                res = await patchShopProductDimension(editing.id, fd);
            } else {
                res = await postShopProductDimension(fd);
            }
            if (res.status === 200 || res.status === 201) {
                toast.success(editing ? "Dimension updated" : "Dimension saved");
                onSaved();
                onClose();
            } else {
                toast.error(res.data?.detail || "Save failed");
            }
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                (typeof err?.response?.data === "string" ? err.response.data : null) ||
                "Could not save dimension";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ShowModal
            isOpen={open}
            onRequestClose={onClose}
            contentLabel={editing ? "Edit Dimension" : "Add Dimension"}
            title={editing ? "Edit Dimension" : "Add Dimension"}
            modalWidth="min(440px, 100vw)"
            isSidebar
        >
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={dimensionSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting }) => (
                    <Form className="flex flex-col gap-5 pb-4">
                        <AsyncFormikDropdown
                            label="Shop Product"
                            name="shop_product"
                            required
                            needSearch
                            debounceTime={300}
                            placeholder="Search product…"
                            isDisabled={Boolean(editing?.id)}
                            initialOptions={initialOptions}
                            formatOptionLabel={shopProductFormatOptionLabel}
                            apiCallFunction={async (input) => {
                                const response = await searchProductWithAkaName(input);
                                return mapShopProductSearchToOptions(response);
                            }}
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <InputField label="Length (cm)" name="length_cm" type="number" step="any" placeholder="0" />
                            <InputField label="Width (cm)" name="width_cm" type="number" step="any" placeholder="0" />
                            <InputField label="Height (cm)" name="height_cm" type="number" step="any" placeholder="0" />
                            <InputField label="Weight (kg)" name="weight_kg" type="number" step="any" placeholder="0" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <DimensionImageField
                                label="Length photo"
                                name="length_photo"
                                file={lengthFile}
                                onFileChange={setLengthFile}
                                existingUrl={editing?.length_photo}
                            />
                            <DimensionImageField
                                label="Width photo"
                                name="width_photo"
                                file={widthFile}
                                onFileChange={setWidthFile}
                                existingUrl={editing?.width_photo}
                            />
                            <DimensionImageField
                                label="Height photo"
                                name="height_photo"
                                file={heightFile}
                                onFileChange={setHeightFile}
                                existingUrl={editing?.height_photo}
                            />
                            <DimensionImageField
                                label="Weight photo"
                                name="weight_photo"
                                file={weightFile}
                                onFileChange={setWeightFile}
                                existingUrl={editing?.weight_photo}
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-6 rounded-lg"
                        >
                            {isSubmitting ? "Saving…" : "Save Dimension"}
                        </Button>
                    </Form>
                )}
            </Formik>
        </ShowModal>
    );
}

function EmptyDimensionsState({ onAdd }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center border border-dashed border-gray-200 rounded-2xl bg-gray-50/80 mt-4">
            <div className="w-28 h-28 mb-4 text-gray-300" aria-hidden>
                <svg viewBox="0 0 120 120" fill="currentColor" className="w-full h-full">
                    <rect x="20" y="35" width="80" height="60" rx="8" fill="none" stroke="currentColor" strokeWidth="3" />
                    <path d="M35 55 L50 40 L70 60 L85 45" stroke="currentColor" strokeWidth="2.5" fill="none" />
                    <circle cx="42" cy="48" r="5" fill="currentColor" opacity="0.5" />
                </svg>
            </div>
            <p className="text-lg font-semibold text-gray-800 mb-1">No dimensions added yet</p>
            <p className="text-sm text-gray-500 mb-6 max-w-sm">Add package dimensions and photos for shop products.</p>
            <Button type="button" onClick={onAdd} className="bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2.5 rounded-lg">
                Add Dimension
            </Button>
        </div>
    );
}

const PAGE_SIZE = 10;

export default function ShopProductDimensions() {
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchText(searchText), 300);
        return () => clearTimeout(t);
    }, [searchText]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchText]);

    const loadDimensions = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            const q = debouncedSearchText.trim();
            if (q) params.set("search", q);
            params.set("page", String(currentPage));
            params.set("page_size", String(PAGE_SIZE));
            const query = `?${params.toString()}`;
            const res = await getShopProductDimensions(query);
            if (res.status !== 200) {
                throw new Error("Bad status");
            }
            const data = res.data;
            const list = Array.isArray(data) ? data : data?.results ?? [];
            const count = typeof data?.count === "number" ? data.count : list.length;
            const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE) || 1);
            if (currentPage > totalPages) {
                setCurrentPage(totalPages);
                setLoading(false);
                return;
            }
            setRows(list);
            setTotalCount(count);
        } catch (e) {
            console.error(e);
            toast.error(
                <div className="flex flex-col gap-2">
                    <span>Could not load product dimensions.</span>
                    <button
                        type="button"
                        className="text-left text-sm font-semibold underline"
                        onClick={() => {
                            toast.dismiss();
                            loadDimensions();
                        }}
                    >
                        Retry
                    </button>
                </div>,
                { autoClose: 8000 }
            );
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchText, currentPage]);

    useEffect(() => {
        loadDimensions();
    }, [loadDimensions]);

    const resetSearch = () => {
        setSearchText("");
        setDebouncedSearchText("");
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    /** Pagination bar expects a `data` object; omit setData so prev/next use page numbers + refetch */
    const paginationData = useMemo(
        () => ({ results: rows, count: totalCount, next: null, previous: null }),
        [rows, totalCount]
    );

    const rowNumberOffset = (currentPage - 1) * PAGE_SIZE;

    const openAdd = () => {
        setEditing(null);
        setDrawerOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setEditing(null);
    };

    const handleDelete = async (row) => {
        const name = row.product_name || "this product";
        const ok = await confirmDanger(
            `Are you sure you want to delete dimensions for ${name}?`,
            "Delete dimensions"
        );
        if (!ok) return;
        try {
            const res = await deleteShopProductDimension(row.id);
            if (res.status === 200 || res.status === 204) {
                toast.success("Deleted");
                loadDimensions();
            } else {
                toast.error("Delete failed");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    const copyDimensionDetails = async (row) => {
        const text = buildDimensionCopyText(row);
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Dimensions copied to clipboard");
        } catch {
            toast.error("Could not copy — check browser permissions");
        }
    };

    const handleDownloadPhotosZip = async (row) => {
        const tid = toast.loading("Preparing ZIP…");
        try {
            await downloadDimensionPhotosZip(row);
            toast.dismiss(tid);
            toast.success("ZIP downloaded (folder: product name inside archive)");
        } catch (e) {
            toast.dismiss(tid);
            if (e?.message === "NO_PHOTOS") {
                toast.warning("No photos to download for this row");
            } else {
                toast.error("Could not build ZIP — try again or open images in a new tab");
            }
        }
    };

    const headers = [
        { label: "#", key: "idx", width: "48px" },
        { label: "Product", key: "product" },
        { label: "Dimensions (L x W x H)", key: "dims" },
        { label: "Weight", key: "weight" },
        { label: "Photos", key: "photos" },
        { label: "Copy / ZIP", key: "export" },
        { label: "Actions", key: "actions" },
    ];

    const tableRows = rows.map((row, index) => ({
        idx: <span className="text-sm text-gray-600">{rowNumberOffset + index + 1}</span>,
        product: (
            <div className="flex items-center gap-3 min-w-0 max-w-xs">
                <img
                    src={resolveMediaUrl(row.product_image) || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect fill='%23e5e7eb' width='40' height='40'/%3E%3C/svg%3E"}
                    alt=""
                    className="h-10 w-10 rounded-md object-cover border border-gray-100 flex-shrink-0"
                />
                <span className="text-sm font-medium text-gray-900 truncate" title={row.product_name}>
                    {row.product_name || "—"}
                </span>
            </div>
        ),
        dims: <span className="text-sm text-gray-800 whitespace-nowrap">{formatDimensionsRow(row)}</span>,
        weight: (
            <span className="text-sm text-gray-800">
                {row.weight_kg != null ? `${Number(row.weight_kg).toFixed(2)} kg` : "—"}
            </span>
        ),
        photos: (
            <div className="flex items-center gap-1 flex-wrap">
                <PhotoThumbButton url={row.length_photo} onOpen={setPreviewUrl} title="Length photo" />
                <PhotoThumbButton url={row.width_photo} onOpen={setPreviewUrl} title="Width photo" />
                <PhotoThumbButton url={row.height_photo} onOpen={setPreviewUrl} title="Height photo" />
                <PhotoThumbButton url={row.weight_photo} onOpen={setPreviewUrl} title="Weight photo" />
            </div>
        ),
        export: (
            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                <button
                    type="button"
                    onClick={() => copyDimensionDetails(row)}
                    className="p-2 rounded-lg text-gray-700 hover:bg-gray-100 border border-transparent hover:border-gray-200"
                    title="Copy dimensions & weight to clipboard"
                >
                    <BiCopy size={20} />
                </button>
                <button
                    type="button"
                    onClick={() => handleDownloadPhotosZip(row)}
                    className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-50 border border-transparent hover:border-emerald-200"
                    title="Download all photos in one ZIP (folder named after product)"
                >
                    <Archive size={20} strokeWidth={2} />
                </button>
            </div>
        ),
        actions: (
            <div className="flex items-center gap-2 justify-center">
                <button
                    type="button"
                    onClick={() => openEdit(row)}
                    className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"
                    title="Edit"
                >
                    <BiEdit size={22} />
                </button>
                <button
                    type="button"
                    onClick={() => handleDelete(row)}
                    className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                    title="Delete"
                >
                    <BiTrash size={22} />
                </button>
            </div>
        ),
    }));

    return (
        <>
            <Breadcrumbs items={[{ name: "Home", link: "/dashboard" }, { name: "Bazaar" }, { name: "Product Dimensions" }]} />
            <div className="py-3 flex flex-col gap-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <StyledHeadingText text="Product Dimensions" />
                    <div className="flex flex-wrap items-center gap-2 flex-1 justify-end min-w-[min(100%,280px)]">
                        <div className="flex items-center gap-2 flex-1 max-w-md min-w-[200px]">
                            <SearchBar
                                placeholder="Search by product name…"
                                onSearch={setSearchText}
                                value={searchText}
                            />
                            {searchText ? (
                                <button
                                    type="button"
                                    onClick={resetSearch}
                                    className="flex-shrink-0 p-1 rounded-lg hover:bg-gray-100 text-primary"
                                    title="Clear search"
                                >
                                    <BiRefresh size={24} />
                                </button>
                            ) : null}
                        </div>
                        <Button
                            type="button"
                            onClick={openAdd}
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg"
                        >
                            Add Dimension
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <TableSkeletonRows cols={7} rows={8} />
                ) : rows.length === 0 ? (
                    debouncedSearchText.trim() ? (
                        <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 py-12 px-4 text-center">
                            <p className="text-gray-700">
                                No dimensions match &quot;{debouncedSearchText.trim()}&quot;.
                            </p>
                            <button
                                type="button"
                                onClick={resetSearch}
                                className="mt-3 text-sm font-semibold text-primary hover:underline"
                            >
                                Clear search
                            </button>
                        </div>
                    ) : (
                        <EmptyDimensionsState onAdd={openAdd} />
                    )
                ) : (
                    <>
                        <div className="mt-2 overflow-x-auto">
                            <Table headers={headers} rows={tableRows} />
                        </div>
                        {totalCount > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                pageSize={PAGE_SIZE}
                                totalEntries={totalCount}
                                onPageChange={handlePageChange}
                                data={paginationData}
                            />
                        )}
                    </>
                )}
            </div>

            <DimensionFormDrawer
                open={drawerOpen}
                onClose={closeDrawer}
                editing={editing}
                onSaved={loadDimensions}
            />

            {previewUrl && (
                <button
                    type="button"
                    className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/80 p-4 border-0 cursor-zoom-out"
                    onClick={() => setPreviewUrl(null)}
                    aria-label="Close preview"
                >
                    <img
                        src={previewUrl}
                        alt="Preview"
                        className="max-h-[90vh] max-w-full object-contain rounded-lg shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </button>
            )}
        </>
    );
}
